import * as React from 'react';
import { dataSource } from '@/data';
import type { IncidenteResumo } from '@/data/types';

/**
 * A lista de incidentes (`IncidenteResumo`) traz só `total_versoes`; as versões
 * em si vêm do detalhe. O card as pede de forma preguiçosa — só quando entra na
 * tela — e guarda por `id` + `atualizado_em`, então rolar para cima e para baixo
 * não repete a chamada e uma edição invalida o cache sozinha.
 */
const cache = new Map<string, string[]>();

export function limparCacheVersoesDoIncidente(): void {
  cache.clear();
}

export function useVersoesDoIncidente(incidente: IncidenteResumo): {
  ref: React.RefObject<HTMLElement>;
  versoes: string[] | null;
} {
  const chave = `${incidente.id}:${incidente.atualizado_em}:${incidente.total_versoes}`;
  const ref = React.useRef<HTMLElement>(null);
  const [versoes, setVersoes] = React.useState<string[] | null>(() => cache.get(chave) ?? null);

  React.useEffect(() => {
    const emCache = cache.get(chave);
    if (emCache) {
      setVersoes(emCache);
      return;
    }
    setVersoes(null);
    const controller = new AbortController();
    function buscar() {
      dataSource
        .obterIncidente(incidente.id, controller.signal)
        .then((detalhe) => {
          const lista = detalhe.versoes.map((v) => v.versao);
          cache.set(chave, lista);
          setVersoes(lista);
        })
        .catch(() => {
          // Sem a lista o card mostra só o total de versões; nada a tratar aqui.
        });
    }
    const alvo = ref.current;
    if (!alvo || typeof IntersectionObserver === 'undefined') {
      buscar();
      return () => controller.abort();
    }
    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((entrada) => entrada.isIntersecting)) {
          observador.disconnect();
          buscar();
        }
      },
      { rootMargin: '200px 0px' },
    );
    observador.observe(alvo);
    return () => {
      observador.disconnect();
      controller.abort();
    };
  }, [chave, incidente.id]);

  return { ref, versoes };
}
